import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Mui from "@mui/material";
import { AddRounded, CheckCircleRounded, ContentCopyOutlined, ErrorOutlineRounded } from "@mui/icons-material";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { Field } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { EmptyRow, Required, SimpleTable, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { ChipMultiSelect, PlainInput, StatutoryPage, formatInr } from "pages/PayRoll/Shared/statutoryUi";
import {
  ActiveVersionCard,
  ComponentsTable,
  SettingsCard,
  SettingsGrid,
  VersionDetailsCard,
  VersionFooter,
  VersionHistoryPanel,
  VersionStatusBanner,
  useVersionForm,
} from "pages/PayRoll/Shared/statutoryConfigUi";
import {
  AMOUNT_TYPE_OPTIONS,
  APPLICABILITY_OPTIONS,
  COVERAGE_BASIS_OPTIONS,
  FREQUENCY_OPTIONS,
  LOW_PAY_OPTIONS,
  MONTHLY_SET_DATE_OPTIONS,
  MONTH_OPTIONS,
  REQUIRED_CODES,
  ROUNDING_OPTIONS,
  activeVersion,
  bandCheck,
  bandsSummary,
  bandsWithTo,
  formatDay,
  monthName,
  monthsForFrequency,
  optionLabel,
  setDateOptions,
  sortMonths,
  syncSetDates,
  tableFormulaOf,
  validateSiteVersion,
} from "./lwfConfigData";
import {
  copyLwfDraftFromSite,
  createLwfDraftFrom,
  createLwfVersion,
  discardLwfDraft,
  fetchLwfDefaults,
  fetchLwfSite,
  fetchLwfTestEmployees,
  saveLwfDraft,
  updateLwfVersion,
} from "./lwfConfigApi";
import { LwfBandPanel } from "./LwfBandPanel";
import { LwfComponentPanel } from "./LwfComponentPanel";
import { usePayrollSettingsCrumbs } from "../../payrollSettingsMenu";

// Site LWF Configuration (LWF1a): one site's LWF rules with versions —
// version details, site and registration, who pays, contribution bands and
// components. Built on the shared statutory version parts (same as ESI)
// and saved through the LWF API (lwfConfigApi.js); a draft on the server
// is auto-saved while it is edited. Not applicable / Exempt hide
// everything after the basis.

const REQUIRED = new Set(REQUIRED_CODES);
// A monthly site uses one day for every month (1–28, so it is inside every
// month); "last day" can't be sent as one day for all months.
const MONTHLY_DAY_OPTIONS = MONTHLY_SET_DATE_OPTIONS.filter((option) => option.value !== "LAST");

// Auto-save state of the draft: "", "saving", "saved" or "failed".
const AUTOSAVE_TEXT = {
  saving: "Saving the draft…",
  saved: "Draft saved. You can come back to it later.",
  failed: "The draft couldn't be saved automatically. Your changes are still here.",
};

// State comes from the site (Administration › Sites); it can't be picked here.
const validateForm = (form, versions) => {
  const errors = validateSiteVersion(form, versions);
  if (errors.state) errors.state = "Set the site's state in Administration › Sites";
  return errors;
};

const historyFieldsFor = (categoryLabel) => [
  ["applicability", "Does LWF apply", (value) => optionLabel(APPLICABILITY_OPTIONS, value)],
  ["applicability_basis", "Basis"],
  ["state", "State"],
  ["registration_no", "LWF registration no."],
  ["frequency", "How often deducted", (value) => optionLabel(FREQUENCY_OPTIONS, value)],
  ["deduction_months", "Deduct in", (value) => (value || []).map(monthName).join(", ") || "–"],
  ["payment_due_days", "Payment due (days)"],
  ["coverage_basis", "Covered based on", (value) => optionLabel(COVERAGE_BASIS_OPTIONS, value)],
  ["set_dates", "Set date", (value) => (value || []).map((day) => (day === "LAST" ? "Last day" : day)).join(", ") || "–"],
  ["wage_limit", "Not covered above wage (₹)", (value) => value || "No limit"],
  ["excluded_categories", "Categories not covered", (value) => (value || []).map(categoryLabel).join(", ") || "None"],
  ["low_pay_treatment", "If pay is too low", (value) => optionLabel(LOW_PAY_OPTIONS, value)],
  ["amount_type", "Amount type", (value) => optionLabel(AMOUNT_TYPE_OPTIONS, value)],
  ["rounding", "Rounding", (value) => optionLabel(ROUNDING_OPTIONS, value)],
  ["bands", "Bands", (value) => bandsText(value || [])],
];

// Bands in words; a percentage band has its % filled and no fixed amount.
function bandsText(bands) {
  const percent = bands.some((band) => band.employee_pct !== "" && band.employee_amount === "");
  return bandsSummary(bands, percent ? "PERCENT_CAP" : "FIXED_BAND");
}

// ---------------- Contribution bands ----------------

const BandsCard = ({ form, locked, onAdd, onOpen }) => {
  const percent = form.amount_type === "PERCENT_CAP";
  const rows = bandsWithTo(form.bands);
  const check = bandCheck(form.bands, form.amount_type);
  const money = (value) => (value === "" || value == null ? "–" : formatInr(value));
  const heads = percent
    ? [
        { label: "#", width: 40 },
        { label: "Wage from (₹)", width: 120 },
        { label: "Wage to (₹)", width: 120 },
        { label: "Employee %", width: 100 },
        { label: "Employee max (₹)", width: 130 },
        { label: "Employer × employee", width: 150 },
        { label: "Employer max (₹)", width: 130 },
        { label: "Actions", width: 110 },
      ]
    : [
        { label: "#", width: 40 },
        { label: "Wage from (₹)", width: 140 },
        { label: "Wage to (₹)", width: 140 },
        { label: "Employee (₹)", width: 120 },
        { label: "Employer (₹)", width: 120 },
        { label: "Total (₹)", width: 100 },
        { label: "Actions", width: 110 },
      ];

  return (
    <SettingsCard
      title={`Contribution Bands (${rows.length})`}
      info="Amount per employee each time LWF is deducted · one band if the amount is the same for everyone"
      action={
        <SecondaryButton startIcon={<AddRounded />} onClick={onAdd} disabled={locked} sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
          Add Band
        </SecondaryButton>
      }
    >
      <SimpleTable fit heads={heads}>
        {rows.length ? (
          rows.map((row, index) => (
            <Mui.TableRow key={row.id} hover>
              <Mui.TableCell sx={tableCellSx}>{index + 1}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{money(row.wage_from)}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{row.wage_to === "" ? "and above" : money(row.wage_to)}</Mui.TableCell>
              {percent ? (
                <>
                  <Mui.TableCell sx={tableCellSx}>{row.employee_pct === "" ? "–" : `${row.employee_pct}%`}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{money(row.employee_max)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{row.employer_multiple === "" ? "–" : `${row.employer_multiple}×`}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>{money(row.employer_max)}</Mui.TableCell>
                </>
              ) : (
                <>
                  <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{money(row.employee_amount)}</Mui.TableCell>
                  <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.semibold }}>{money(row.employer_amount)}</Mui.TableCell>
                  <Mui.TableCell sx={tableCellSx}>
                    {row.employee_amount === "" || row.employer_amount === "" ? "–" : formatInr(Number(row.employee_amount) + Number(row.employer_amount))}
                  </Mui.TableCell>
                </>
              )}
              <Mui.TableCell sx={tableCellSx}>
                <SecondaryButton onClick={() => onOpen(row)} sx={{ minWidth: 0, minHeight: 32, px: 1.5 }}>
                  {locked ? "View" : "Edit"}
                </SecondaryButton>
              </Mui.TableCell>
            </Mui.TableRow>
          ))
        ) : (
          <EmptyRow colSpan={heads.length} text="No bands yet." />
        )}
      </SimpleTable>
      <Mui.Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
        {check.valid ? <CheckCircleRounded sx={{ fontSize: 16, color: "#12B76A" }} /> : <ErrorOutlineRounded sx={{ fontSize: 16, color: "#DC2626" }} />}
        <Mui.Typography sx={{ ...designType.captionText, color: check.valid ? "#027A48" : "#DC2626" }}>{check.message}</Mui.Typography>
      </Mui.Stack>
    </SettingsCard>
  );
};

// ---------------- Version form ----------------

const SiteVersionForm = ({ domain, companyId, site, versions, setVersions, selectedId, setSelectedId, hasSaved, categoryOptions, persist }) => {
  const autosaveTimer = useRef(null);
  const autosaveRequest = useRef(null);
  const stopAutosave = () => {
    clearTimeout(autosaveTimer.current);
    autosaveRequest.current?.abort();
  };
  const version = useVersionForm({
    versions,
    setVersions,
    selectedId,
    setSelectedId,
    validate: validateForm,
    persist: {
      save: (form, flags) => {
        stopAutosave();
        return persist.save(form, flags);
      },
      discard: (form) => {
        stopAutosave();
        return persist.discard(form);
      },
    },
  });
  const { form, set, setComponents, error, locked, isDraft, active, dirty } = version;
  const [bandPanel, setBandPanel] = useState({ open: false, band: null });
  const [componentPanel, setComponentPanel] = useState({ open: false, mode: "edit", component: null });
  const [autosave, setAutosave] = useState("");
  const [employees, setEmployees] = useState(null);

  // A draft on the server (not the unsaved set-up template) is saved 1.5 s
  // after the last change; only formats are checked there.
  const serverDraft = Boolean(form) && isDraft && typeof form.id === "number";
  const formText = form ? JSON.stringify(form) : "";
  useEffect(() => {
    if (!serverDraft || !dirty) return undefined;
    clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      autosaveRequest.current?.abort();
      const request = new AbortController();
      autosaveRequest.current = request;
      setAutosave("saving");
      saveLwfDraft({ domain, companyId, siteId: site.id, form: JSON.parse(formText), signal: request.signal })
        .then(() => setAutosave("saved"))
        .catch((err) => {
          if (err.name !== "AbortError") setAutosave("failed");
        });
    }, 1500);
    return () => clearTimeout(autosaveTimer.current);
  }, [serverDraft, dirty, formText, domain, companyId, site.id]);
  useEffect(
    () => () => {
      clearTimeout(autosaveTimer.current);
      autosaveRequest.current?.abort();
    },
    [],
  );
  const formId = form?.id;
  useEffect(() => setAutosave(""), [formId]);

  // Employees for "Test on employee" (LWF employees list, this site first),
  // loaded the first time a band or component drawer opens.
  const testOpen = bandPanel.open || componentPanel.open;
  useEffect(() => {
    if (!testOpen || employees) return undefined;
    const request = new AbortController();
    fetchLwfTestEmployees({ domain, companyId, siteId: site.id, signal: request.signal })
      .then(setEmployees)
      .catch((err) => {
        if (err.name !== "AbortError") setEmployees([]);
      });
    return () => request.abort();
  }, [testOpen, employees, domain, companyId, site.id]);

  if (!form) return null;
  const covered = form.applicability === "COVERED";

  const setMonths = (months, dates) => {
    const sorted = sortMonths(months);
    set("deduction_months")(sorted);
    set("set_dates")(dates || syncSetDates(sorted, form.deduction_months, form.set_dates));
  };
  // Monthly: one day for every month, picked again (1–28).
  const setFrequency = (frequency) => {
    set("frequency")(frequency);
    const months = monthsForFrequency(frequency, form.deduction_months);
    setMonths(months, frequency === "MONTHLY" ? months.map(() => (form.frequency === "MONTHLY" ? form.set_dates[0] || "" : "")) : undefined);
  };
  const setSetDate = (index) => (day) => {
    if (!day) return;
    set("set_dates")(form.frequency === "MONTHLY" ? form.deduction_months.map(() => day) : form.set_dates.map((value, position) => (position === index ? day : value)));
  };

  const saveBand = (band) => {
    set("bands")(form.bands.some((item) => item.id === band.id) ? form.bands.map((item) => (item.id === band.id ? band : item)) : [...form.bands, band]);
    setBandPanel((current) => ({ ...current, open: false }));
  };
  const saveComponent = (component) => {
    setComponents((list) =>
      componentPanel.mode === "add" ? [...list, component] : list.map((item) => (item.code === componentPanel.component.code ? component : item)),
    );
    setComponentPanel((current) => ({ ...current, open: false }));
  };
  // Delete from the drawer (the tables have no ⋮ menu).
  const deleteBand = (band) => {
    set("bands")(form.bands.filter((item) => item.id !== band.id));
    setBandPanel((current) => ({ ...current, open: false }));
  };
  const deleteComponent = (component) => {
    setComponents((list) => list.filter((item) => item.code !== component.code));
    setComponentPanel((current) => ({ ...current, open: false }));
  };

  // Deduct in: one month (Yearly), two (Half-yearly) or every month.
  const deductInField = !form.frequency ? (
    <PlainInput value="" placeholder="Pick how often first" onChange={() => {}} disabled />
  ) : form.frequency === "MONTHLY" ? (
    <PlainInput value="Every month" onChange={() => {}} disabled />
  ) : form.frequency === "YEARLY" ? (
    <SelectField
      value={form.deduction_months[0] || ""}
      onChange={(value) => value && setMonths([value])}
      options={MONTH_OPTIONS}
      disabled={locked}
      error={error("deduction_months")}
    />
  ) : (
    <ChipMultiSelect
      value={form.deduction_months}
      onChange={setMonths}
      options={MONTH_OPTIONS.map((option) => ({
        ...option,
        disabled: form.deduction_months.length >= 2 && !form.deduction_months.includes(option.value),
      }))}
      placeholder="Pick 2 months"
      disabled={locked}
      error={error("deduction_months")}
    />
  );

  // Set date: one per deduction month (one for every month when Monthly).
  const setDateField =
    form.coverage_basis !== "SET_DATE" ? (
      <PlainInput value="" placeholder={form.coverage_basis ? "Not used — any day of the month counts" : "Pick how coverage is decided first"} onChange={() => {}} disabled />
    ) : !form.deduction_months.length ? (
      <PlainInput value="" placeholder="Pick the deduction month(s) first" onChange={() => {}} disabled />
    ) : form.frequency === "MONTHLY" ? (
      <SelectField value={form.set_dates[0] || ""} onChange={setSetDate(0)} options={MONTHLY_DAY_OPTIONS} disabled={locked} error={error("set_dates")} />
    ) : (
      <Mui.Stack spacing={1}>
        {form.deduction_months.map((month, index) => (
          <SelectField
            key={month}
            value={form.set_dates[index] || ""}
            onChange={setSetDate(index)}
            options={setDateOptions(month)}
            disabled={locked}
            error={error("set_dates")}
          />
        ))}
      </Mui.Stack>
    );

  const banner =
    isDraft && (!hasSaved || form.copied_from) ? (
      <RequestInfoBanner
        tone="purple"
        title={form.copied_from ? `Draft — copied from ${form.copied_from}. Not used by payroll until saved.` : "Draft — not used by payroll until saved."}
        body={
          form.copied_from
            ? "Check the registration number, months and amounts for this site, then save."
            : "Record whether LWF applies to this site, fill in its rules and pick the month they start. Nothing is deducted for this site until you save."
        }
      />
    ) : (
      <VersionStatusBanner form={form} active={active} locked={locked} isDraft={isDraft} itemLabel="LWF" onOpenActive={() => setSelectedId(active.id)} />
    );

  return (
    <Mui.Stack spacing={2}>
      {banner}
      <VersionDetailsCard form={form} locked={locked} isDraft={isDraft} set={set} error={error} />

      <SettingsGrid columns={{ xs: "1fr", lg: covered ? "1fr 1fr" : "1fr" }}>
        <SettingsCard title="Site and registration" info="Employees working at this site on the last day of the month pay LWF here">
          <SettingsGrid columns={{ xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }}>
            <Field label="Site">
              <PlainInput value={site.name} onChange={() => {}} disabled />
            </Field>
            <Field label={<>State<Required /></>} error={error("state")}>
              <PlainInput value={form.state || site.state} placeholder="From the site" onChange={() => {}} disabled error={error("state")} />
            </Field>
            {covered ? (
              <>
                <Field label={<>LWF registration no.<Required /></>} error={error("registration_no")}>
                  <PlainInput
                    value={form.registration_no}
                    onChange={set("registration_no")}
                    disabled={locked}
                    error={error("registration_no")}
                    placeholder="e.g. TN/LWF/0045821"
                    inputProps={{ maxLength: 30 }}
                  />
                </Field>
                <Field label={<>How often LWF is deducted<Required /></>} error={error("frequency")}>
                  <SelectField value={form.frequency} onChange={(value) => value && setFrequency(value)} options={FREQUENCY_OPTIONS} disabled={locked} error={error("frequency")} />
                </Field>
                <Field label={<>Deduct in<Required /></>} error={error("deduction_months")}>
                  {deductInField}
                </Field>
                <Field label={<>Payment due — days after the deduction month<Required /></>} error={error("payment_due_days")}>
                  <PlainInput
                    type="number"
                    value={form.payment_due_days}
                    onChange={set("payment_due_days")}
                    disabled={locked}
                    error={error("payment_due_days")}
                    placeholder="e.g. 31"
                    inputProps={{ min: 0, max: 365 }}
                  />
                </Field>
              </>
            ) : null}
            <Field label={<>Does LWF apply to this site?<Required /></>} error={error("applicability")}>
              <SelectField
                value={form.applicability}
                onChange={(value) => value && set("applicability")(value)}
                options={APPLICABILITY_OPTIONS}
                disabled={locked}
                error={error("applicability")}
              />
            </Field>
            <Field label={<>Establishment type / basis<Required /></>} error={error("applicability_basis")}>
              <PlainInput
                value={form.applicability_basis}
                onChange={set("applicability_basis")}
                disabled={locked}
                error={error("applicability_basis")}
                placeholder="e.g. Factory · 25+ employees"
              />
            </Field>
            <Field label="Confirmed by">
              <PlainInput
                value={form.confirmed_by ? `${form.confirmed_by} · ${formatDay(form.confirmed_at)}` : ""}
                placeholder="Recorded when you save"
                onChange={() => {}}
                disabled
              />
            </Field>
          </SettingsGrid>
          <Mui.Box sx={{ mt: 2 }}>
            <RequestInfoBanner body="Options: Covered (LWF applies) · Not applicable (LWF does not apply) · Exempt (the site is exempt). Your company decides; the basis is required for all three. Half-yearly: two months (for example June and December). Yearly: one month." />
          </Mui.Box>
        </SettingsCard>

        {covered ? (
          <SettingsCard title="Who pays" info="Leave blank what your state does not use">
            <SettingsGrid columns={{ xs: "1fr", sm: "1fr 1fr" }}>
              <Field label={<>Covered based on<Required /></>} error={error("coverage_basis")}>
                <SelectField
                  value={form.coverage_basis}
                  onChange={(value) => value && set("coverage_basis")(value)}
                  options={COVERAGE_BASIS_OPTIONS}
                  disabled={locked}
                  error={error("coverage_basis")}
                />
              </Field>
              <Field label={<>Set date{form.coverage_basis === "SET_DATE" ? <Required /> : null}</>} error={error("set_dates")}>
                {setDateField}
              </Field>
              <Field label="Not covered above wage (₹ / month, optional)" error={error("wage_limit")}>
                <PlainInput
                  type="number"
                  adornment="₹"
                  value={form.wage_limit}
                  onChange={set("wage_limit")}
                  disabled={locked}
                  error={error("wage_limit")}
                  placeholder="No limit"
                  inputProps={{ min: 0 }}
                />
              </Field>
              <Field label="Not covered: employee categories (optional)">
                <ChipMultiSelect
                  value={form.excluded_categories}
                  onChange={set("excluded_categories")}
                  options={categoryOptions}
                  placeholder="+ Add"
                  disabled={locked}
                />
              </Field>
              <Mui.Box sx={{ gridColumn: "1 / -1" }}>
                <SettingsGrid columns={{ xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }}>
                  <Field label={<>If pay is too low to deduct<Required /></>} error={error("low_pay_treatment")}>
                    <SelectField
                      value={form.low_pay_treatment}
                      onChange={(value) => value && set("low_pay_treatment")(value)}
                      options={LOW_PAY_OPTIONS}
                      disabled={locked}
                      error={error("low_pay_treatment")}
                    />
                  </Field>
                  <Field label={<>Amount type<Required /></>} error={error("amount_type")}>
                    <SelectField
                      value={form.amount_type}
                      onChange={(value) => value && set("amount_type")(value)}
                      options={AMOUNT_TYPE_OPTIONS}
                      disabled={locked}
                      error={error("amount_type")}
                    />
                  </Field>
                  <Field label="Rounding">
                    <SelectField value={form.rounding} onChange={(value) => value && set("rounding")(value)} options={ROUNDING_OPTIONS} disabled={locked} />
                  </Field>
                </SettingsGrid>
              </Mui.Box>
            </SettingsGrid>
            <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
              Covered based on: on the rolls on a set date, or employed on any day of the deduction month. If pay is too low: record the employee share as
              outstanding, or the company pays it — the employer share is always due. Amount type: fixed amount by wage band, or percentage of wage with a
              maximum (employer = multiple of the employee share, with its own maximum). Only these two patterns are supported.
            </Mui.Typography>
          </SettingsCard>
        ) : null}
      </SettingsGrid>

      {covered ? (
        <>
          <BandsCard
            form={form}
            locked={locked}
            onAdd={() => setBandPanel({ open: true, band: null })}
            onOpen={(band) => setBandPanel({ open: true, band: form.bands.find((item) => item.id === band.id) })}
          />
          {["CALCULATION", "CONTRIBUTION"].map((kind) => (
            <ComponentsTable
              key={kind}
              title={kind === "CALCULATION" ? "Calculation Components" : "Contribution Components"}
              info={kind === "CALCULATION" ? "Wage LWF is worked out on" : "Employee deduction and employer cost"}
              kind={kind}
              components={form.components}
              locked={locked}
              requiredCodes={REQUIRED}
              formulaOf={tableFormulaOf}
              versionName={form.name}
              onAdd={kind === "CALCULATION" ? () => setComponentPanel({ open: true, mode: "add", component: null }) : undefined}
              onOpen={(component) => setComponentPanel({ open: true, mode: "edit", component })}
              onChange={setComponents}
              showMenu={false}
            />
          ))}
        </>
      ) : form.applicability ? (
        <RequestInfoBanner
          title={`${site.name} is marked ${optionLabel(APPLICABILITY_OPTIONS, form.applicability)}.`}
          body="No LWF is worked out for employees at this site and Validation raises no issue. The basis and who confirmed it are kept with this version."
        />
      ) : null}

      {serverDraft && autosave ? (
        <Mui.Typography sx={{ ...designType.captionText, textAlign: "right", color: autosave === "failed" ? "#DC2626" : designColors.slate600 }}>
          {AUTOSAVE_TEXT[autosave]}
        </Mui.Typography>
      ) : null}
      <VersionFooter
        form={form}
        active={active}
        locked={locked}
        isDraft={isDraft}
        dirty={dirty}
        saving={version.saving}
        onCancel={version.reset}
        onSave={version.save}
        onDiscard={version.discard}
      />

      <LwfBandPanel
        open={bandPanel.open}
        band={bandPanel.band}
        form={form}
        siteName={site.name}
        readOnly={locked}
        domain={domain}
        companyId={companyId}
        siteId={site.id}
        employees={employees}
        onClose={() => setBandPanel((current) => ({ ...current, open: false }))}
        onSave={saveBand}
        onDelete={form.bands.length > 1 ? deleteBand : undefined}
      />
      <LwfComponentPanel
        open={componentPanel.open}
        mode={componentPanel.mode}
        component={componentPanel.component}
        form={form}
        siteName={site.name}
        readOnly={locked}
        server={{ domain, companyId, siteId: site.id, employees }}
        onClose={() => setComponentPanel((current) => ({ ...current, open: false }))}
        onSave={saveComponent}
        onDelete={deleteComponent}
      />
    </Mui.Stack>
  );
};

// ---------------- Page ----------------

export const LwfSiteConfig = ({ domain, companyId, siteId, siteRows, options, onBack, onSitesChanged }) => {
  const settingsCrumbs = usePayrollSettingsCrumbs();
  const [site, setSite] = useState(() => {
    const row = siteRows.find((item) => item.id === siteId);
    return { id: siteId, name: row?.site || "", code: row?.code || "", state: row?.state || "" };
  });
  const [versions, setVersions] = useState([]);
  const [load, setLoad] = useState({ loading: true, error: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFromId, setCopyFromId] = useState("");
  const controller = useRef(null);

  // (Re)load the site's versions and draft. Keeps the selection if it still
  // exists, else `selectId` (the version just saved) or the active version.
  // A site with nothing saved and no draft opens the empty set-up form.
  const loadSite = useCallback(
    async (selectId) => {
      controller.current?.abort();
      const request = new AbortController();
      controller.current = request;
      setLoad((state) => ({ ...state, loading: true, error: "" }));
      try {
        const data = await fetchLwfSite({ domain, companyId, siteId, signal: request.signal });
        let list = data.versions;
        if (!list.length) list = [await fetchLwfDefaults({ domain, companyId, siteId, signal: request.signal })];
        setSite((current) => ({ ...current, ...Object.fromEntries(Object.entries(data.site).filter(([, value]) => value)) }));
        setVersions(list);
        setSelectedId((currentId) => {
          const wanted = selectId ?? currentId;
          if (list.some((version) => version.id === wanted)) return wanted;
          return activeVersion(list.filter((version) => version.status !== "Draft"))?.id ?? list[list.length - 1]?.id ?? null;
        });
        setLoad({ loading: false, error: "" });
      } catch (err) {
        if (err.name === "AbortError") return;
        setLoad({ loading: false, error: err.message || "Couldn't load this site's LWF configuration." });
      }
    },
    [domain, companyId, siteId],
  );

  useEffect(() => {
    if (companyId) loadSite();
    return () => controller.current?.abort();
  }, [loadSite, companyId]);

  const saved = versions.filter((version) => version.status !== "Draft");
  const hasSaved = saved.length > 0;
  const active = activeVersion(saved);
  const draft = versions.find((version) => version.status === "Draft");
  const copySources = siteRows.filter((row) => row.set_up && row.id !== siteId);

  // Employee categories: the company's list, plus any a saved version still
  // names (so an old category shows with its label).
  const categoryOptions = useMemo(() => {
    const list = [...(options?.categories || [])];
    versions.forEach((version) =>
      Object.entries(version.category_labels || {}).forEach(([value, label]) => {
        if (!list.some((option) => option.value === value)) list.push({ value, label });
      }),
    );
    return list;
  }, [options, versions]);
  const historyFields = useMemo(
    () => historyFieldsFor((id) => categoryOptions.find((option) => option.value === id)?.label || id),
    [categoryOptions],
  );

  // Create New Version copies the active version into the site's draft;
  // when a draft already exists it is opened instead.
  const createVersion = async () => {
    if (draft) {
      setSelectedId(draft.id);
      return;
    }
    const base = active || saved[saved.length - 1];
    if (!base || busy) return;
    setBusy(true);
    try {
      const { version, message } = await createLwfDraftFrom({ domain, companyId, siteId, versionId: base.id });
      showToast("success", message || `${version.name} created as a draft.`);
      setVersions((list) => [...list.filter((item) => item.status !== "Draft" && item.id !== "new"), version]);
      setSelectedId(version.id);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setBusy(false);
    }
  };

  const copyFromSite = async () => {
    const source = copySources.find((row) => String(row.id) === copyFromId);
    if (!source || busy) return;
    setBusy(true);
    try {
      const { version, message } = await copyLwfDraftFromSite({ domain, companyId, siteId, sourceSiteId: source.id });
      setCopyOpen(false);
      showToast("success", message || `Rules copied from ${source.site} into a draft. Not used by payroll until saved.`);
      setVersions((list) => [...list.filter((item) => item.status !== "Draft" && item.id !== "new"), version]);
      setSelectedId(version.id);
    } catch (err) {
      showToast("error", err.message);
    } finally {
      setBusy(false);
    }
  };

  // Save (draft or set-up form -> new version) or Save changes (saved,
  // unlocked version). A 409 means the version changed or got locked:
  // reload so the screen shows it.
  const persist = {
    save: async (form, { isDraft }) => {
      try {
        const result = isDraft
          ? await createLwfVersion({ domain, companyId, siteId, form })
          : await updateLwfVersion({ domain, companyId, form });
        if (isDraft && form.id !== "new") await discardLwfDraft({ domain, companyId, siteId }).catch(() => {});
        showToast("success", result.message || `${result.version.name} saved.`);
        await loadSite(result.version.id);
        onSitesChanged();
      } catch (err) {
        if (err.status === 409) loadSite(form.id);
        throw err;
      }
    },
    discard: async (form) => {
      if (form.id !== "new") await discardLwfDraft({ domain, companyId, siteId });
      showToast("success", "Draft discarded.");
      if (hasSaved) await loadSite(active?.id);
      else onBack();
    },
  };

  const copyButton = (
    <SecondaryButton
      startIcon={<ContentCopyOutlined />}
      onClick={() => {
        setCopyFromId(copySources[0] ? String(copySources[0].id) : "");
        setCopyOpen(true);
      }}
      disabled={!copySources.length || busy}
      sx={{ whiteSpace: "nowrap" }}
    >
      Copy from site
    </SecondaryButton>
  );
  const copySource = copySources.find((row) => String(row.id) === copyFromId);
  const title = site.name ? `${site.name} — LWF Configuration` : "LWF Configuration";

  return (
    <StatutoryPage
      breadcrumb={settingsCrumbs("Statutory", { label: "Labour Welfare Fund", onClick: onBack }, site.name || "Site")}
      title={title}
      subtitle="LWF rules for this site. Create a new version when the amounts or rules change."
      onBack={onBack}
      aside={
        load.loading && !versions.length ? null : hasSaved ? (
          <ActiveVersionCard version={active} hasDraft={Boolean(draft)} onHistory={() => setHistoryOpen(true)} onCreate={createVersion} extra={copyButton} />
        ) : (
          copyButton
        )
      }
    >
      {load.error ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load this site's LWF configuration."
          body={load.error}
          action={
            <SecondaryButton onClick={() => loadSite()} sx={{ whiteSpace: "nowrap" }}>
              Retry
            </SecondaryButton>
          }
        />
      ) : load.loading && !versions.length ? (
        <Mui.Box role="status" aria-label="Loading the site's LWF configuration">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : (
        <SiteVersionForm
          domain={domain}
          companyId={companyId}
          site={site}
          versions={versions}
          setVersions={setVersions}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          hasSaved={hasSaved}
          categoryOptions={categoryOptions}
          persist={persist}
        />
      )}

      <VersionHistoryPanel
        open={historyOpen}
        versions={versions}
        selectedId={selectedId}
        fields={historyFields}
        formulaOf={tableFormulaOf}
        subtitle={`${site.name} · LWF rules by month. A version used by a payroll is locked.`}
        note="A version used by a payroll is locked. To change anything, create a new version from a new month. The whole month uses the version in force on the 1st, under the employee's site on the last day of the month."
        onClose={() => setHistoryOpen(false)}
        onOpenVersion={(id) => {
          setSelectedId(id);
          setHistoryOpen(false);
        }}
      />

      <GlobalConfirmDialog
        open={copyOpen}
        title="Copy from site"
        onCancel={() => setCopyOpen(false)}
        onConfirm={copyFromSite}
        confirmLabel={busy ? "Copying..." : "Copy"}
        cancelLabel="Cancel"
        confirmDisabled={!copyFromId || busy}
        maxWidth="sm"
      >
        <Mui.Stack spacing={2}>
          <Mui.Typography sx={designType.helperText}>
            Copies the rules, bands and components of another site's current version into a draft for {site.name}
            {draft ? ` (replacing what is in ${draft.name})` : ""}. The registration number and the basis are not copied. Nothing is used by payroll until
            you save.
          </Mui.Typography>
          <Field label="Copy from">
            <SelectField
              value={copyFromId}
              onChange={setCopyFromId}
              options={copySources.map((row) => ({
                value: String(row.id),
                label: [row.site, row.version_label, row.status_label].filter(Boolean).join(" · "),
              }))}
              disablePortal={false}
            />
          </Field>
          {copySource ? (
            <Mui.Typography sx={designType.captionText}>
              {[shown(copySource.state), shown(copySource.frequency_label), shown(copySource.deduct_in_label)].filter(Boolean).join(" · ")}
            </Mui.Typography>
          ) : null}
        </Mui.Stack>
      </GlobalConfirmDialog>
    </StatutoryPage>
  );
};

// Blank or "—" values are left out of the copy summary line.
function shown(value) {
  return value && value !== "—" ? value : "";
}
